import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Modal, Progress, Spin, Upload, message } from 'antd';
import type { RcFile, UploadChangeParam, UploadFile } from 'antd/es/upload/interface';
import dayjs, { type Dayjs } from 'dayjs';
import { formatFileSize } from '@wavvest/shared-utils';
import { useFirmDataEnabled } from '@/hooks/useFirmDataEnabled';
import { useAuth } from '@/contexts/AuthContext';
import { getItemWithExpiry } from '@/util/util';
import {
  ApexDocument,
  FilehubFilesData,
  confirmUploadFile,
  deleteEnduserFile,
  getAdvisorFilesWithShares,
  getApexDocuments,
  getFilehubPresignedURL,
  getReceivedAdvisorFilesWithShares,
  getSharedFileDownloadLink,
} from '@/api/custom';
import { checkUserSignedUp, getTaxReturnFiles } from '@/services/tax-service';
import './index.scss';

// A tax return that exists for a given year (federal or a state return).
// The actual download link is fetched fresh on click (April links expire ~5 min).
interface TaxReturnDoc {
  key: string;
  year: number;
  name: string;
  type: 'federal' | 'state';
}

type DocumentFilter = 'all' | 'advisor' | 'statements' | 'tax' | 'uploaded';

type DocumentCategory = 'Shared' | 'Statement' | 'Confirmation' | 'Account' | 'Tax' | 'Uploaded';

/** One row of the Documents table, whatever it came from. */
type DocumentRow = {
  key: string;
  name: string;
  category: DocumentCategory;
  filter: Exclude<DocumentFilter, 'all'>;
  from: string;
  date: Dayjs | null;
  /** Newest first; tax returns have no date, so they sort by tax season. */
  sortKey: number;
  size?: number;
  download: () => void;
  /** Only the investor's own uploads can be deleted. */
  remove?: () => void;
};

const FILTERS: { key: DocumentFilter; label: string; short: string }[] = [
  { key: 'all', label: 'All documents', short: 'All' },
  { key: 'advisor', label: 'From your advisor', short: 'From advisor' },
  { key: 'statements', label: 'Account documents', short: 'Account' },
  { key: 'tax', label: 'Tax documents', short: 'Tax' },
  { key: 'uploaded', label: 'Uploaded by you', short: 'Uploaded' },
];

const PAGE_SIZE = 10;

type ApexDatePayload = { year: number; month: number; day: number };

const apexDocumentType = (doc: ApexDocument): string | undefined =>
  doc.investor_document?.document_type ??
  doc.account_document?.document_type ??
  doc.id_document?.document_type;

const apexDatePayload = (doc: ApexDocument): ApexDatePayload | undefined =>
  doc.investor_document?.process_date ??
  doc.account_document?.uploaded_date ??
  doc.id_document?.uploaded_date;

const fromDatePayload = (payload?: ApexDatePayload): Dayjs | null =>
  payload
    ? dayjs()
        .year(payload.year)
        .month(payload.month - 1)
        .date(payload.day)
    : null;

/** "Account Statement, September 2026" from Apex's type and date. */
const apexDocumentName = (doc: ApexDocument): string => {
  const type = apexDocumentType(doc);
  const typeLabel = type
    ? type
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, (c) => c.toUpperCase())
    : 'Document';
  const date = fromDatePayload(apexDatePayload(doc));
  return [typeLabel, date?.format('MMMM YYYY')].filter(Boolean).join(', ');
};

// Apex files tax forms (1099s, 5498s, 1042-S) among statements, trade
// confirmations and account forms (applications, ID).
const apexCategory = (doc: ApexDocument): DocumentCategory => {
  const type = apexDocumentType(doc) ?? '';
  if (/TAX|1099|5498|1042|IRS/i.test(type)) return 'Tax';
  if (/STATEMENT/i.test(type)) return 'Statement';
  if (/CONFIRM/i.test(type)) return 'Confirmation';
  return 'Account';
};

const DownloadIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    aria-hidden="true"
  >
    <path d="M12 4v12M6 10l6 6 6-6M4 20h16" />
  </svg>
);

const DeleteIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    aria-hidden="true"
  >
    <path d="M4 7h16M10 11v6M14 11v6M6 7l1 12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-12M9 7V4h6v3" />
  </svg>
);

const UploadIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    aria-hidden="true"
  >
    <path d="M12 16V4M6 10l6-6 6 6M4 20h16" />
  </svg>
);

const SearchIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    aria-hidden="true"
  >
    <circle cx="11" cy="11" r="7" />
    <path d="M20 20l-3.5-3.5" />
  </svg>
);

/**
 * Documents (boards Documents, MDocuments): advisor files, Apex statements and
 * tax forms, tax returns and the investor's own uploads in one table, with a
 * filter list, search and secure upload.
 */
const FileHub: React.FC = () => {
  const { user } = useAuth();
  const [advisorId, setAdvisorId] = useState('');
  const { enabled: firmDataEnabled, loading: firmDataLoading } = useFirmDataEnabled(
    advisorId || undefined,
  );

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sentFiles, setSentFiles] = useState<FilehubFilesData[]>([]);
  const [receivedFiles, setReceivedFiles] = useState<FilehubFilesData[]>([]);
  const [apexDocuments, setApexDocuments] = useState<ApexDocument[]>([]);
  const [taxDocuments, setTaxDocuments] = useState<TaxReturnDoc[]>([]);

  const [filter, setFilter] = useState<DocumentFilter>('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [isMobile, setIsMobile] = useState(false);

  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [pendingFile, setPendingFile] = useState<RcFile | null>(null);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const loadRequestRef = useRef(0);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mql = window.matchMedia('(max-width: 768px)');
    setIsMobile(mql.matches);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    const userStr = getItemWithExpiry('user');
    const storedUser = userStr ? JSON.parse(userStr) : null;
    setAdvisorId(storedUser?.client_id || '');
  }, []);

  const fetchAllFiles = async (
    fetchPage: typeof getAdvisorFilesWithShares,
  ): Promise<FilehubFilesData[]> => {
    let files: FilehubFilesData[] = [];
    let paginationKey = '';
    let hasMore = true;
    while (hasMore) {
      const response = await fetchPage({
        limit: String(PAGE_SIZE),
        pagination_key: paginationKey,
        sort_order: 'desc',
        include_share_details: 'true',
      });
      files = [...files, ...response.files];
      paginationKey = response.pagination_key;
      hasMore = response.has_more;
    }
    return files;
  };

  // Prior-year April tax returns (federal/state). Self-gates on April signup so
  // non-tax users fetch nothing.
  const fetchTaxDocuments = async (email: string): Promise<TaxReturnDoc[]> => {
    try {
      const isSignedUp = await checkUserSignedUp(email);
      if (!isSignedUp) return [];

      const currentYear = new Date().getFullYear();
      // Most recent filed year back to 2024 (matches the mobile vault floor).
      const years = Array.from(
        { length: Math.max(0, currentYear - 2024) },
        (_, i) => currentYear - 1 - i,
      );
      const perYear = await Promise.all(
        years.map(async (year) => {
          try {
            const res = await getTaxReturnFiles(email, 'FILING', year.toString());
            return { year, files: res.files };
          } catch {
            // No return for this year (or not available yet).
            return null;
          }
        }),
      );

      const docs: TaxReturnDoc[] = [];
      perYear.forEach((entry) => {
        if (!entry?.files) return;
        const { year, files } = entry;
        if (files.federalFileLink) {
          docs.push({
            key: `${year}-federal`,
            year,
            name: `${year} Federal Tax Return`,
            type: 'federal',
          });
        }
        if (files.isStateFileNeeded && files.stateFileLink) {
          docs.push({
            key: `${year}-state`,
            year,
            name: `${year} State Tax Return`,
            type: 'state',
          });
        }
      });
      return docs;
    } catch (taxError) {
      console.error('Error fetching tax documents:', taxError);
      return [];
    }
  };

  const fetchData = async () => {
    const requestId = loadRequestRef.current + 1;
    loadRequestRef.current = requestId;
    setLoading(true);
    setError(null);
    try {
      const [sent, received, apex, tax] = await Promise.all([
        fetchAllFiles(getAdvisorFilesWithShares),
        fetchAllFiles(getReceivedAdvisorFilesWithShares),
        firmDataEnabled
          ? Promise.resolve({ document_records: [] as ApexDocument[] })
          : getApexDocuments().catch(() => ({ document_records: [] as ApexDocument[] })),
        user?.email ? fetchTaxDocuments(user.email) : Promise.resolve([]),
      ]);
      if (loadRequestRef.current !== requestId) return;
      setSentFiles(sent);
      setReceivedFiles(received);
      setApexDocuments(
        apex.document_records.filter(
          (doc) => doc.id_document?.document_type !== 'THIRD_PARTY_CIP_RESULTS',
        ),
      );
      setTaxDocuments(tax);
    } catch (loadError) {
      console.error('Error fetching files:', loadError);
      if (loadRequestRef.current === requestId) {
        setError("We couldn't load your documents. Please try again.");
      }
    } finally {
      if (loadRequestRef.current === requestId) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (advisorId && !firmDataLoading) {
      void fetchData();
    }
    // user?.email is included so tax documents load once the auth session
    // hydrates (AuthContext resolves user asynchronously, possibly after advisorId).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firmDataEnabled, firmDataLoading, advisorId, user?.email]);

  const handleFileDownload = async (record: FilehubFilesData) => {
    try {
      const downloadLinkResponse = await getSharedFileDownloadLink(record.key);
      // The backend's download_url embeds the raw filename in the S3
      // response-content-disposition header, which S3 rejects with
      // InvalidArgument when the name contains non ISO-8859-1 characters
      // (e.g. Unicode spaces in macOS screenshots). Fetch the object via the
      // preview_url (no content-disposition) and save it client-side so the
      // filename is applied locally and every file type downloads reliably.
      const response = await fetch(downloadLinkResponse.preview_url);
      if (!response.ok) {
        throw new Error(`Download failed with status ${response.status}`);
      }
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = record.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    } catch (downloadError) {
      console.error('Failed to download shared file:', downloadError);
      message.error('Failed to download file');
    }
  };

  const handleDelete = (record: FilehubFilesData) => {
    Modal.confirm({
      className: 'documents-modal',
      title: 'Delete this file?',
      content: `${record.filename} will be removed for you and your advisor.`,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Keep',
      onOk: async () => {
        try {
          await deleteEnduserFile(record.key);
          setSentFiles((prev) => prev.filter((file) => file.key !== record.key));
          message.success(`${record.filename} deleted`);
        } catch (deleteError) {
          console.error('Delete failed:', deleteError);
          message.error(`Failed to delete ${record.filename}`);
        }
      },
    });
  };

  const handleApexDownload = (record: ApexDocument) => {
    const uri = record.download_uri ?? record.download_link;
    if (!uri) {
      message.error('Download URL not available for this document');
      return;
    }
    window.open(uri, '_blank', 'noopener,noreferrer');
  };

  // April download links expire after ~5 minutes, so fetch a fresh link on
  // click rather than reusing the one loaded with the page.
  const handleTaxDownload = async (record: TaxReturnDoc) => {
    if (!user?.email) {
      message.error('Unable to get user information');
      return;
    }
    // Open the tab synchronously (within the click gesture) so it isn't
    // popup-blocked, then point it at the freshly generated link.
    const downloadWindow = window.open('about:blank', '_blank');
    try {
      const res = await getTaxReturnFiles(user.email, 'FILING', record.year.toString());
      const link = record.type === 'federal' ? res.files.federalFileLink : res.files.stateFileLink;
      if (!link) {
        downloadWindow?.close();
        message.error('Download link is no longer available.');
        return;
      }
      if (downloadWindow) {
        downloadWindow.opener = null;
        downloadWindow.location.href = link;
      } else {
        window.open(link, '_blank', 'noopener,noreferrer');
      }
    } catch (taxError) {
      console.error('Failed to download tax document:', taxError);
      downloadWindow?.close();
      message.error('Failed to download tax document. Please try again.');
    }
  };

  const rows = useMemo<DocumentRow[]>(() => {
    const all: DocumentRow[] = [
      ...receivedFiles.map<DocumentRow>((file) => ({
        key: `received-${file.key}`,
        name: file.filename,
        category: 'Shared',
        filter: 'advisor',
        from: 'Your advisor',
        date: file.upload_at ? dayjs(file.upload_at) : null,
        sortKey: file.upload_at ? dayjs(file.upload_at).valueOf() : 0,
        size: file.size_byte,
        download: () => void handleFileDownload(file),
      })),
      ...sentFiles.map<DocumentRow>((file) => ({
        key: `sent-${file.key}`,
        name: file.filename,
        category: 'Uploaded',
        filter: 'uploaded',
        from: 'You',
        date: file.upload_at ? dayjs(file.upload_at) : null,
        sortKey: file.upload_at ? dayjs(file.upload_at).valueOf() : 0,
        size: file.size_byte,
        download: () => void handleFileDownload(file),
        remove: () => handleDelete(file),
      })),
      ...apexDocuments.map<DocumentRow>((doc, index) => {
        const category = apexCategory(doc);
        const date = fromDatePayload(apexDatePayload(doc));
        return {
          key: `apex-${doc.document_id ?? doc.name ?? index}`,
          name: apexDocumentName(doc),
          category,
          filter: category === 'Tax' ? 'tax' : 'statements',
          from: 'Apex',
          date,
          sortKey: date?.valueOf() ?? 0,
          download: () => handleApexDownload(doc),
        };
      }),
      ...taxDocuments.map<DocumentRow>((doc) => ({
        key: `tax-${doc.key}`,
        name: doc.name,
        category: 'Tax',
        filter: 'tax',
        from: 'Tax filing',
        date: null,
        sortKey: dayjs(`${doc.year + 1}-04-15`).valueOf(),
        download: () => void handleTaxDownload(doc),
      })),
    ];
    return all.sort((a, b) => b.sortKey - a.sortKey);
    // The handlers only read current state when clicked.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receivedFiles, sentFiles, apexDocuments, taxDocuments, user?.email]);

  const filters = FILTERS.filter((option) => option.key !== 'statements' || !firmDataEnabled);
  const counts = useMemo(() => {
    const result: Record<DocumentFilter, number> = {
      all: rows.length,
      advisor: 0,
      statements: 0,
      tax: 0,
      uploaded: 0,
    };
    rows.forEach((row) => {
      result[row.filter] += 1;
    });
    return result;
  }, [rows]);

  const keyword = search.trim().toLowerCase();
  const visible = rows.filter(
    (row) =>
      (filter === 'all' || row.filter === filter) &&
      (!keyword || row.name.toLowerCase().includes(keyword)),
  );
  const pageCount = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  // Phones load more onto one list; wider screens page through the table.
  const shown = isMobile
    ? visible.slice(0, currentPage * PAGE_SIZE)
    : visible.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const firstShown = visible.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const lastShown = Math.min(currentPage * PAGE_SIZE, visible.length);

  const chooseFilter = (next: DocumentFilter) => {
    setFilter(next);
    setPage(1);
  };

  const closeUpload = () => {
    if (uploading) return;
    setUploadOpen(false);
    setPendingFile(null);
    setFileList([]);
    setUploadProgress(0);
  };

  const handleUploadChange = (info: UploadChangeParam<UploadFile>) => {
    setFileList(
      info.fileList.slice(-1).map((file) => (file.response ? { ...file, status: 'done' } : file)),
    );
  };

  const handleUploadFile = async (file: RcFile) => {
    setUploading(true);
    setUploadProgress(0);
    const fail = (text: string) => {
      message.error(text);
      setUploading(false);
    };
    try {
      const presigned = await getFilehubPresignedURL(
        {
          is_public: false,
          requires_document_processing: true,
          shares: advisorId ? [advisorId] : [],
          file_name: file.name,
          file_size: file.size,
          file_type: file.type,
        },
        file,
      );

      const formData = new FormData();
      Object.entries(presigned.presigned_body.fields).forEach(([key, value]) => {
        formData.append(key, value);
      });
      formData.append('file', file);

      const xhr = new XMLHttpRequest();
      xhr.open('POST', presigned.presigned_body.url, true);
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          setUploadProgress(Math.round((event.loaded / event.total) * 100));
        }
      };
      xhr.onload = async () => {
        if (xhr.status < 200 || xhr.status >= 300) {
          console.error('Upload failed with status:', xhr.status);
          fail(`Upload failed with status: ${xhr.status}`);
          return;
        }
        try {
          const response = await confirmUploadFile(
            presigned.data.key,
            file.name,
            presigned.data.owner_cognito_id,
            presigned.data.shares || [],
          );
          setSentFiles((prev) => [response.file, ...prev]);
          message.success(`${file.name} uploaded`);
          setUploading(false);
          setUploadOpen(false);
          setPendingFile(null);
          setFileList([]);
          setUploadProgress(0);
        } catch (confirmError) {
          console.error('Confirm upload failed:', confirmError);
          fail(`Failed to confirm upload for ${file.name}`);
        }
      };
      xhr.onerror = () => {
        console.error('Network error during upload');
        fail('Network error during upload');
      };
      xhr.send(formData);
    } catch (prepareError) {
      console.error('Upload preparation failed:', prepareError);
      fail(`${file.name} upload preparation failed`);
    }
  };

  const renderActions = (row: DocumentRow) => (
    <span className="documents-actions">
      {row.remove && (
        <button
          type="button"
          className="documents-icon-button"
          aria-label={`Delete ${row.name}`}
          onClick={row.remove}
        >
          <DeleteIcon />
        </button>
      )}
      <button
        type="button"
        className="documents-icon-button"
        aria-label={`Download ${row.name}`}
        onClick={row.download}
      >
        <DownloadIcon />
      </button>
    </span>
  );

  const renderTable = () => (
    <div className="documents-table-scroll">
      <table className="documents-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Category</th>
            <th>From</th>
            <th>Date</th>
            <th className="is-number">Size</th>
            <th>
              <span className="documents-sr">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {shown.map((row) => (
            <tr key={row.key}>
              <td className="documents-name">{row.name}</td>
              <td>
                <span className="documents-category">{row.category}</span>
              </td>
              <td>{row.from}</td>
              <td className="documents-mono">{row.date ? row.date.format('MMM D, YYYY') : '—'}</td>
              <td className="documents-mono is-number">
                {row.size !== undefined ? formatFileSize(row.size) : '—'}
              </td>
              <td className="is-number">{renderActions(row)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderList = () => (
    <ul className="documents-list">
      {shown.map((row) => (
        <li key={row.key}>
          <span className="documents-list-copy">
            <span className="documents-name">{row.name}</span>
            <small>
              {[row.category, row.from, row.date?.format('MMM D')].filter(Boolean).join(' · ')}
            </small>
          </span>
          {renderActions(row)}
        </li>
      ))}
    </ul>
  );

  const emptyText = keyword
    ? `No documents match “${search.trim()}”.`
    : filter === 'all'
      ? 'No documents yet. Statements, tax forms and files your advisor shares will show here.'
      : 'Nothing here yet.';

  return (
    <div className="documents-page">
      <header className="documents-head">
        <div>
          <div className="documents-eyebrow">Statements, tax forms and shared files</div>
          <h1>Documents</h1>
        </div>
        <div className="documents-tools">
          <label className="documents-search">
            <SearchIcon />
            <span className="documents-sr">Search documents</span>
            <input
              type="search"
              placeholder="Search documents"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
            />
          </label>
          <Button
            type="primary"
            className="documents-upload"
            icon={<UploadIcon />}
            aria-label="Upload securely"
            onClick={() => setUploadOpen(true)}
          >
            <span className="documents-upload-label">Upload securely</span>
          </Button>
        </div>
      </header>

      {!advisorId && (
        <Alert
          type="warning"
          showIcon
          message="Your account doesn't have an advisor yet, so uploads can't be shared. Please contact your advisor."
        />
      )}
      {error && (
        <Alert
          type="error"
          showIcon
          message={error}
          action={<Button onClick={() => void fetchData()}>Try again</Button>}
        />
      )}

      <div className="documents-body">
        <nav className="documents-filters" aria-label="Document filters">
          {filters.map((option) => (
            <button
              key={option.key}
              type="button"
              className={option.key === filter ? 'is-active' : ''}
              aria-pressed={option.key === filter}
              onClick={() => chooseFilter(option.key)}
            >
              <span className="documents-filter-label">{option.label}</span>
              <span className="documents-filter-short">{option.short}</span>
              <span className="documents-mono documents-filter-count">{counts[option.key]}</span>
            </button>
          ))}
        </nav>

        <section className="documents-card" aria-label="Documents" aria-busy={loading}>
          {loading && rows.length === 0 ? (
            <div className="documents-loading">
              <Spin />
            </div>
          ) : visible.length === 0 ? (
            <p className="documents-empty">{emptyText}</p>
          ) : isMobile ? (
            renderList()
          ) : (
            renderTable()
          )}

          {visible.length > 0 && !isMobile && (
            <div className="documents-pager">
              <span>
                Showing {firstShown}–{lastShown} of {visible.length}
              </span>
              <span className="documents-pager-buttons">
                <Button disabled={currentPage <= 1} onClick={() => setPage(currentPage - 1)}>
                  Previous
                </Button>
                <Button
                  disabled={currentPage >= pageCount}
                  onClick={() => setPage(currentPage + 1)}
                >
                  Next
                </Button>
              </span>
            </div>
          )}
        </section>
        {isMobile && currentPage < pageCount && (
          <Button className="documents-more" onClick={() => setPage(currentPage + 1)}>
            Load more
          </Button>
        )}
      </div>

      <Modal
        className="documents-modal"
        title="Upload securely"
        open={uploadOpen}
        onCancel={closeUpload}
        maskClosable={!uploading}
        footer={[
          <Button key="cancel" onClick={closeUpload} disabled={uploading}>
            Cancel
          </Button>,
          <Button
            key="upload"
            type="primary"
            loading={uploading}
            disabled={!pendingFile}
            onClick={() => pendingFile && void handleUploadFile(pendingFile)}
          >
            Upload
          </Button>,
        ]}
        width={520}
      >
        <p className="documents-modal-copy">Files you upload are shared with your advisor.</p>
        {uploading && <Progress percent={uploadProgress} status="active" />}
        <Upload.Dragger
          customRequest={({ file, onSuccess }) => {
            setPendingFile(file as RcFile);
            onSuccess?.('ok');
          }}
          fileList={fileList}
          onChange={handleUploadChange}
          multiple={false}
          disabled={uploading}
        >
          <p className="documents-drop-icon">
            <UploadIcon />
          </p>
          <p className="ant-upload-text">Drag a file here or click to choose one</p>
          <p className="ant-upload-hint">Up to 100 MB</p>
        </Upload.Dragger>
      </Modal>
    </div>
  );
};

export default FileHub;
